import React from 'react';
import {
    escapeRegExp,
    ListItem,
    Preloader,
    SuggestInput,
    SuggestInputItem,
    Typography
} from '@genestack/ui';
import {useAvailableTagsQuery} from './requests/use-available-tags-query';
import {useDocumentTagsQuery} from './requests/use-document-tags-query';
import {useAddTagMutation} from './requests/use-add-tag-mutation';

interface Props {
    docAccession: string;
    isPublic: boolean;
}

export function TagsInput(props: Props) {
    const [searchString, setSearchString] = React.useState('');
    const clearSearch = React.useCallback(() => setSearchString(''), []);

    React.useEffect(() => {
        clearSearch();
    }, [clearSearch, props.docAccession]);

    const availableTagsQuery = useAvailableTagsQuery();
    const documentTagsQuery = useDocumentTagsQuery(props.docAccession);
    const addTagMutation = useAddTagMutation();

    function renderSuggestItem(tag: string, isNewTag?: boolean) {
        return (
            <SuggestInputItem key={tag} value={tag}>
                <Typography variant={isNewTag ? 'section' : 'body'} box="inline">
                    {isNewTag ? `Create tag "${tag}"` : tag}
                </Typography>
            </SuggestInputItem>
        );
    }

    const initialTagsList = React.useMemo(() => {
        const tags = props.isPublic
            ? availableTagsQuery.data?.publicTags
            : availableTagsQuery.data?.privateTags;

        return tags && tags.sort((tag1, tag2) => (tag1 < tag2 ? -1 : 1));
    }, [availableTagsQuery.data, props.isPublic]);

    const suggestedContent = React.useMemo(() => {
        const notFoundElement = (
            <ListItem>
                <Typography intent="quiet" variant="caption">
                    Cannot add a tag with this name
                </Typography>
            </ListItem>
        );

        const documentTagsList = props.isPublic
            ? documentTagsQuery.data?.publicTags
            : documentTagsQuery.data?.privateTags;

        if (!initialTagsList || !documentTagsList) {
            return null;
        }

        const filteredTags = initialTagsList
            .filter((tag) => !documentTagsList.includes(tag))
            .filter((tag) => tag.toLowerCase().match(escapeRegExp(searchString.toLowerCase())));

        const isSearchStringUniqueTag =
            !!searchString.length &&
            !initialTagsList.find(
                (tag) => tag.toLowerCase().trim() === searchString.toLowerCase().trim()
            );

        if (!isSearchStringUniqueTag && !filteredTags.length) {
            return notFoundElement;
        }

        return [
            ...filteredTags.map((tag) => renderSuggestItem(tag)),
            ...(isSearchStringUniqueTag
                ? [renderSuggestItem(searchString.toUpperCase(), true)]
                : [])
        ];
    }, [initialTagsList, documentTagsQuery.data, props.isPublic, searchString]);

    if (!availableTagsQuery.data || !documentTagsQuery.data) {
        return <Preloader show count={1} box="paragraph" />;
    }

    return (
        <SuggestInput
            id="share-query-user-selector"
            clearable={!!searchString}
            fullWidth
            value={searchString}
            openOnFocus
            onValueChange={setSearchString}
            placeholder="Enter tag name"
            onClearButtonClick={clearSearch}
            onComplete={(tag: string) => {
                addTagMutation.mutate({
                    accession: props.docAccession,
                    tag,
                    isPrivate: !props.isPublic
                });
                clearSearch();
            }}
        >
            {suggestedContent}
        </SuggestInput>
    );
}
